import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import {
  currentEnv,
  datadogApplicationId,
  datadogClientToken,
  datadogSite,
  isDevelopment,
} from "./environment";

const DATADOG_SERVICE = import.meta.env.VITE_DATADOG_SERVICE;
const SLOW_API_REQUEST_MS = 1500;
let datadogInitialized = false;

const getReleaseVersion = (): string => {
  const v = import.meta.env.VITE_BUILD_NUMBER;
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : "0";
};
const now = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

const normalizeError = (
  error: unknown
): { message: string; stack?: string } => {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  return { message: "Unknown error" };
};

// Errors that do not originate from our code and are not actionable: browser
// extensions (MetaMask, cashback/coupon helpers, translation add-ons), the
// cross-origin "Script error." with no usable stack, and the benign
// ResizeObserver loop notice. We drop these before they reach Datadog so the
// real, fixable errors are not buried in third-party noise.
const NOISE_MESSAGE_PATTERNS = [
  "ResizeObserver loop",
  "Script error.",
  "Could not establish connection. Receiving end does not exist",
  "Failed to connect to MetaMask",
  "cashbackReminder",
];

const NOISE_STACK_PATTERNS = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
];

const isThirdPartyNoise = (message?: string, stack?: string): boolean => {
  const msg = message ?? "";
  if (NOISE_MESSAGE_PATTERNS.some((p) => msg.includes(p))) return true;
  const stk = stack ?? "";
  return NOISE_STACK_PATTERNS.some((p) => stk.includes(p));
};

const isDatadogEnabled = () =>
  !isDevelopment &&
  Boolean(datadogApplicationId) &&
  Boolean(datadogClientToken);

const registerGlobalErrorHandlers = () => {
  window.addEventListener("error", (event) => {
    const normalizedError = normalizeError(event.error ?? event.message);

    if (isThirdPartyNoise(normalizedError.message, normalizedError.stack)) {
      return;
    }

    datadogLogs.logger.error("Unhandled runtime error", {
      source: "window.error",
      message: normalizedError.message,
      stack: normalizedError.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });

    datadogRum.addError(event.error ?? normalizedError.message, {
      source: "window.error",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const normalizedError = normalizeError(event.reason);

    if (isThirdPartyNoise(normalizedError.message, normalizedError.stack)) {
      return;
    }

    datadogLogs.logger.error("Unhandled promise rejection", {
      source: "window.unhandledrejection",
      message: normalizedError.message,
      stack: normalizedError.stack,
    });

    datadogRum.addError(event.reason ?? normalizedError.message, {
      source: "window.unhandledrejection",
    });
  });
};

export const initDatadogRum = () => {
  if (datadogInitialized || !isDatadogEnabled()) return;
  datadogInitialized = true;

  datadogLogs.init({
    clientToken: datadogClientToken,
    site: datadogSite,
    service: DATADOG_SERVICE,
    env: currentEnv,
    version: getReleaseVersion(),
    forwardErrorsToLogs: false,
    sessionSampleRate: 100,
    beforeSend: (event) => {
      if (
        event.status === "error" &&
        isThirdPartyNoise(
          event.message,
          (event as { error?: { stack?: string } }).error?.stack
        )
      ) {
        return false;
      }
      return true;
    },
  });

  datadogRum.init({
    applicationId: datadogApplicationId,
    clientToken: datadogClientToken,
    site: datadogSite,
    service: DATADOG_SERVICE,
    env: currentEnv,
    version: getReleaseVersion(),
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackResources: false,
    trackLongTasks: true,
    trackUserInteractions: false,
    beforeSend: (event) => {
      if (
        event.type === "error" &&
        isThirdPartyNoise(event.error.message, event.error.stack)
      ) {
        return false;
      }
      return true;
    },
  });

  registerGlobalErrorHandlers();
};

type ApiLogPayload = {
  method?: string;
  url?: string;
  status?: number;
  durationMs: number;
  errorMessage?: string;
};

export const trackApiRequest = ({
  method,
  url,
  status,
  durationMs,
  errorMessage,
}: ApiLogPayload) => {
  if (!datadogInitialized) return;

  const payload = {
    method: (method || "UNKNOWN").toUpperCase(),
    url: (url || "").split("?")[0],
    status: status ?? 0,
    duration_ms: Math.round(durationMs),
  };

  datadogRum.addAction("api_request", payload);

  const isServerError = payload.status >= 500 || payload.status === 0;
  const isClientError = payload.status >= 400 && payload.status < 500;
  const isSlowRequest = payload.duration_ms >= SLOW_API_REQUEST_MS;

  if (isServerError || (payload.status === 0 && errorMessage)) {
    datadogLogs.logger.error("API request failed", {
      ...payload,
      error_message: errorMessage || "Unknown API error",
    });
    return;
  }

  if (isClientError) {
    datadogLogs.logger.warn("API client error", payload);
    return;
  }

  if (isSlowRequest) {
    datadogLogs.logger.warn("Slow API request", payload);
  }
};

export const trackWebVital = (name: string, value: number, rating: string) => {
  if (!datadogInitialized) return;

  const payload = { name, value: Number(value.toFixed(2)), rating };
  datadogRum.addAction("web_vital", payload);

  if (rating === "poor") {
    datadogLogs.logger.warn("Poor web vital detected", payload);
  }
};

export const logError = (
  message: string,
  error?: unknown,
  context?: Record<string, unknown>
) => {
  if (!datadogInitialized) return;

  const normalized = error !== undefined ? normalizeError(error) : null;
  const payload = {
    ...context,
    message,
    ...(normalized && {
      error_message: normalized.message,
      stack: normalized.stack,
    }),
  };

  datadogLogs.logger.error(message, payload);
  datadogRum.addError(error ?? message, { ...context, custom: true });
};

type LogContext = Record<string, unknown>;

export const log = {
  info: (message: string, context?: LogContext) => {
    if (!datadogInitialized) return;
    datadogLogs.logger.info(message, context ?? {});
  },
  warn: (message: string, context?: LogContext) => {
    if (!datadogInitialized) return;
    datadogLogs.logger.warn(message, context ?? {});
  },
  error: (message: string, error?: unknown, context?: LogContext) => {
    if (!datadogInitialized) return;
    const normalized = error !== undefined ? normalizeError(error) : null;
    datadogLogs.logger.error(message, {
      ...context,
      ...(normalized && {
        error_message: normalized.message,
        stack: normalized.stack,
      }),
    });
  },
  debug: (message: string, context?: LogContext) => {
    if (!datadogInitialized) return;
    datadogLogs.logger.debug(message, context ?? {});
  },
};

export const getNow = now;
